/**
 * 09.10.26. Не удалять по требованию владельца системы.
 * Heartbeat нотифаера: L2 core и L3 по всем клиникам. Только суперадмин.
 * Страница ничего не отправляет. Открытый контур не предлагает действие.
 */
import { useQuery } from '@tanstack/react-query';
import { MainLayout } from '@/components/layout/MainLayout';
import { apiClient } from '@/api/client';
import { useTenantScope } from '@/hooks/useTenantScope';
import { formatCheckedAt } from '@/features/monitoring/DependencyStrip';
import { EDGE_LABEL, FLOW_TEXT, JOB_LABEL } from '@/features/monitoring/flowCanon';
import { linearCanvas, linearStatusClass } from '@/features/monitoring/linearCanvas';

type HeartbeatItem = {
  key: string;
  name?: string;
  status: string;
  detail?: string;
  level?: string;
  mode?: string;
  checked_at?: string;
};

type HeartbeatTenant = {
  tenant_id: string;
  status: string;
  checked_at?: string;
  infra?: HeartbeatItem[];
  integrations: HeartbeatItem[];
};

type HeartbeatJob = {
  job_key: string;
  status: string;
  last_success_at?: string;
  last_error?: string;
};

type HeartbeatPayload = {
  service: string;
  checked_at: string;
  overall: string;
  core: HeartbeatItem[];
  tenants: HeartbeatTenant[];
  jobs: HeartbeatJob[];
};

const STATUS_LABEL: Record<string, string> = {
  ok: 'в норме',
  warn: 'деградация',
  error: 'недоступно',
  unconfigured: 'не настроено',
  unknown: 'нет данных',
};

export function HeartbeatPage() {
  const { isSuperAdmin } = useTenantScope();
  const query = useQuery({
    queryKey: ['admin', 'heartbeat'],
    enabled: isSuperAdmin,
    queryFn: async () => {
      const response = await apiClient.get<HeartbeatPayload>('/admin/heartbeat');
      return response.data;
    },
  });

  const overall = query.data?.overall ?? 'unknown';
  const circuitOpen = overall === 'down';

  return (
    <MainLayout headerTitle="Heartbeat" disableMainScroll>
      <div className={linearCanvas.canvas} data-testid="heartbeat-canvas">
        <p className={linearCanvas.eyebrow}>Heartbeat</p>
        <h1 className={linearCanvas.title}>Связи нотифаера</h1>
        <p className={linearCanvas.lead}>Что проверяем и когда это проверяли. Сообщения отсюда не уходят. Не настроенное не красится как норма.</p>
        {isSuperAdmin ? (
          <button type="button" className={linearCanvas.button} onClick={() => query.refetch()} disabled={query.isFetching}>
            Обновить
          </button>
        ) : null}
        {!isSuperAdmin ? (
          <p className={linearCanvas.meta}>Раздел только для суперадмина, по всем клиникам.</p>
        ) : query.isLoading ? (
          <p className={linearCanvas.meta}>Читаем heartbeat...</p>
        ) : query.isError ? (
          <p className={linearCanvas.error}>Не удалось прочитать heartbeat.</p>
        ) : circuitOpen ? (
          <p className={linearCanvas.error}>Контур уведомлений временно недоступен. Действие не предлагается.</p>
        ) : (
          <div className={linearCanvas.stackLg}>
            {overall === 'degraded' && (
              <p className={linearCanvas.warn}>
                Часть боевого пайплайна деградировала. Отправка с этой страницы не предлагается.
              </p>
            )}
            <section className={`${linearCanvas.card} ${linearCanvas.stack}`}>
              <p>{FLOW_TEXT.canon}</p>
              <p>{FLOW_TEXT.template} {FLOW_TEXT.autoreply}</p>
              <p>{FLOW_TEXT.interaction}</p>
              <p>{FLOW_TEXT.okdocLanding}</p>
              <p>{FLOW_TEXT.queue} {FLOW_TEXT.history}</p>
              <p>{FLOW_TEXT.appointments}</p>
              <p>{FLOW_TEXT.birthday}</p>
            </section>
            {query.data?.checked_at ? (
              <p className={linearCanvas.meta}>Общая проверка {formatCheckedAt(query.data.checked_at)}</p>
            ) : null}
            <section className={linearCanvas.grid}>
              {(query.data?.core ?? []).map((item) => (
                <div key={item.key} className={linearCanvas.card}>
                  <div className={linearCanvas.kicker}>L2 система</div>
                  <div className={linearCanvas.cardTitle}>{EDGE_LABEL[item.key] ?? item.key}</div>
                  <div className={linearStatusClass(item.status)}>{STATUS_LABEL[item.status] ?? item.status}{item.detail ? ` · ${item.detail}` : ''}</div>
                  <div className={linearCanvas.meta}>{formatCheckedAt(item.checked_at)}</div>
                </div>
              ))}
              {(query.data?.jobs ?? []).map((job) => (
                <div key={job.job_key} className={linearCanvas.card}>
                  <div className={linearCanvas.kicker}>{job.job_key === 'pipeline_probe' ? 'L3 проба' : 'L3 крон'}</div>
                  <div className={linearCanvas.cardTitle}>{JOB_LABEL[job.job_key] ?? job.job_key}</div>
                  <div className={linearStatusClass(job.status)}>
                    {STATUS_LABEL[job.status] ?? job.status}
                    {job.last_error ? ` · ${job.last_error}` : ''}
                  </div>
                  <div className={linearCanvas.meta}>
                    {job.last_success_at ? `Отработал ${formatCheckedAt(job.last_success_at)}` : 'Время отработки ещё не отмечено'}
                  </div>
                </div>
              ))}
            </section>
            <section className={linearCanvas.stack}>
              {(query.data?.tenants ?? []).map((tenant) => (
                <article key={tenant.tenant_id} className={linearCanvas.card}>
                  <div className={linearCanvas.row}>
                    <h2 className={linearCanvas.cardTitle}>{tenant.tenant_id}</h2>
                    <span className={linearStatusClass(tenant.status)}>
                      {STATUS_LABEL[tenant.status] ?? tenant.status}
                      {tenant.checked_at ? ` · ${formatCheckedAt(tenant.checked_at)}` : ''}
                    </span>
                  </div>
                  <ul className={linearCanvas.stack}>
                    {(tenant.infra ?? []).map((item) => (
                      <li key={`infra-${item.key}`} className={linearCanvas.row}>
                        <span>{EDGE_LABEL[item.key] ?? item.key}{item.name ? ` · ${item.name}` : ''}</span>
                        <span className={linearStatusClass(item.status)}>
                          {STATUS_LABEL[item.status] ?? item.status}
                          {item.detail ? ` · ${item.detail}` : ''}
                          {item.checked_at ? ` · ${formatCheckedAt(item.checked_at)}` : ''}
                        </span>
                      </li>
                    ))}
                    {tenant.integrations.map((item) => (
                      <li key={item.key} className={linearCanvas.row}>
                        <span>{EDGE_LABEL[item.key] ?? item.key}</span>
                        <span className={linearStatusClass(item.status)}>
                          {STATUS_LABEL[item.status] ?? item.status}
                          {item.detail ? ` · ${item.detail}` : ''}
                          {item.checked_at ? ` · ${formatCheckedAt(item.checked_at)}` : ''}
                        </span>
                      </li>
                    ))}
                  </ul>
                </article>
              ))}
            </section>
          </div>
        )}
      </div>
    </MainLayout>
  );
}
